import { useEffect, useContext, useRef } from 'react';
import { Check } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { StepNavigationContext } from './CustomizerLayout';
import useNavPin from './useNavPin';

const PRODUCT_TYPE_IMAGES = {
  cover_and_foam: '/product-type-cover-and-foam.png',
  cover_only: '/product-type-cover-only.png',
  foam_only: '/product-type-foam-only.png',
};

const DEFAULT_OPTIONS = [
  { value: 'cover_and_foam', label: 'Cover & Foam Insert', description: 'Complete cushion with cover and foam' },
  { value: 'cover_only', label: 'Cover Only', description: 'Just the fabric cover, no foam' },
  { value: 'foam_only', label: 'Foam Insert Only', description: 'Just the foam insert, no cover' },
];

const ProductTypeStep = ({
  options = DEFAULT_OPTIONS,
  value,
  onChange,
  productName,
  productDescription,
  productFeatures = [],
  productImages = [],
  navigationNode = null,
}) => {
  // Fall back to context so pages that render <ProductTypeStep /> inside a wrapper
  // (e.g. <div ref={stepContentRef}>...) still get the step's navigation buttons
  // injected into the right-hand card without each page having to forward the prop.
  const contextNavigation = useContext(StepNavigationContext);
  const effectiveNavigationNode = navigationNode ?? contextNavigation;

  // Own sentinel / buttons / spacer refs so the Continue button can snap to the
  // bottom of the viewport when it would otherwise be scrolled out of view —
  // same behaviour as every other customizer step.
  const sentinelRef = useRef(null);
  const buttonsRef = useRef(null);
  const spacerRef = useRef(null);
  useNavPin(sentinelRef, buttonsRef, spacerRef, [!!effectiveNavigationNode]);
  // productImages can be an array (same images for every type) OR an object keyed by
  // product type slug ({ cover_and_foam: [...], cover_only: [...], foam_only: [...] })
  // — in which case we pick the set matching the current selection.
  const isMap = productImages && !Array.isArray(productImages) && typeof productImages === 'object';
  const resolvedImages = (() => {
    if (Array.isArray(productImages)) return productImages;
    if (isMap) return productImages[value] || productImages.cover_and_foam || [];
    return [];
  })();

  // Eagerly preload every src across all product-type variants so switching the
  // selection swaps from the browser cache instantly (no re-download flicker).
  // Runs once when the URL set changes (stable JSON key keeps the effect cheap).
  const allSrcsKey = isMap
    ? Object.values(productImages).flat().map(i => i?.src).filter(Boolean).join('|')
    : (Array.isArray(productImages) ? productImages.map(i => i?.src).filter(Boolean).join('|') : '');
  useEffect(() => {
    if (!allSrcsKey) return;
    allSrcsKey.split('|').forEach((src) => {
      if (!src) return;
      const img = new Image();
      img.src = src;
    });
  }, [allSrcsKey]);

  return (
  <div data-testid="step-product-type" className="w-full">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
      {/* LEFT: Product heading + description (desktop 50% / mobile 100%) */}
      {(productName || productDescription) && (
        <div className="order-1 lg:order-1 bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-card" data-testid="product-intro">
          <h1
            className="text-xl font-serif font-bold text-bush mt-[10px] mb-3 sm:mb-4 leading-tight"
            data-testid="product-heading"
          >
            Custom Made {productName}
          </h1>
          {productDescription && (
            <p className="text-sm text-gray-600 leading-relaxed mb-4 sm:mb-5">
              {productDescription}
            </p>
          )}
          {productFeatures.length > 0 && (
            <ul className="space-y-2 mb-4 sm:mb-5" data-testid="product-features">
              {productFeatures.map((feat, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <Check className="h-4 w-4 text-bush flex-shrink-0 mt-0.5" />
                  <span>{feat}</span>
                </li>
              ))}
            </ul>
          )}
          {isMap ? (
            <div className="grid grid-cols-2 gap-3 relative" data-testid="product-images">
              {Object.entries(productImages).map(([typeKey, imgs]) => (
                imgs.slice(0, 2).map((img, i) => (
                  <div
                    key={`${typeKey}-${i}`}
                    className={`aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 transition-opacity duration-200 ${typeKey === value ? '' : 'hidden'}`}
                    data-testid={`product-image-${typeKey}-${i}`}
                    style={{ gridColumn: typeKey === value ? 'auto' : 'unset' }}
                  >
                    <img
                      src={img.src}
                      alt={img.alt || `${productName} ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                ))
              ))}
            </div>
          ) : (
            resolvedImages.length > 0 && (
              <div className="grid grid-cols-2 gap-3" data-testid="product-images">
                {resolvedImages.slice(0, 2).map((img, i) => (
                  <div
                    key={i}
                    className="aspect-[4/3] rounded-lg overflow-hidden bg-gray-100"
                    data-testid={`product-image-${i}`}
                  >
                    <img
                      src={img.src}
                      alt={img.alt || `${productName} ${i + 1}`}
                      className="w-full h-full object-cover"
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}

      {/* RIGHT: 3 product-type questions */}
      <div className="order-2 lg:order-2 bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-card">
        {!productName && (
          <>
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-2 sm:mt-1">Select Product Type</h2>
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm">Choose what you'd like to order.</p>
          </>
        )}
        {productName && (
          <h2 className="text-lg font-semibold text-[#1A1A1A] mb-3 sm:mb-4 sm:mt-1">Select Product Type</h2>
        )}
        <RadioGroup value={value} onValueChange={onChange} className="grid grid-cols-1 gap-3">
          {options.map((option) => {
            const imgSrc = PRODUCT_TYPE_IMAGES[option.value];
            return (
              <label
                key={option.value}
                className={`relative flex items-center p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${value === option.value ? 'border-bush bg-bush/5' : 'border-gray-200 hover:border-gray-300'}`}
                data-testid={`product-type-${option.value}`}
              >
                <RadioGroupItem value={option.value} className="sr-only" />
                {imgSrc && (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden mr-3 sm:mr-4 flex-shrink-0 bg-gray-50">
                    <img src={imgSrc} alt={option.label} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="font-semibold text-sm sm:text-base">{option.label}</h4>
                  <p className="text-gray-500 text-xs sm:text-sm mt-0.5">{option.description}</p>
                </div>
                {value === option.value && (
                  <div className="w-6 h-6 bg-bush text-white rounded-full flex items-center justify-center ml-2 flex-shrink-0">
                    <Check className="h-4 w-4" />
                  </div>
                )}
              </label>
            );
          })}
        </RadioGroup>
        {navigationNode && (
          <div className="mt-6 pt-6 border-t border-[#e3e0dd]">
            {navigationNode}
          </div>
        )}
        {!navigationNode && effectiveNavigationNode && (
          <div className="mt-6 pt-6 border-t border-[#e3e0dd]">
            {/* Sentinel: zero-height marker BEFORE buttons so its position is stable */}
            <div ref={sentinelRef} style={{ height: 0, overflow: 'hidden' }} />
            <div ref={buttonsRef}>
              {effectiveNavigationNode}
            </div>
            {/* Spacer to prevent layout jump when buttons go fixed */}
            <div ref={spacerRef} />
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

export default ProductTypeStep;
